/**
 * Spaces.jsx — the external member's view of case conversations.
 *
 * A dental lab (or outside office) invited into a case by a practice lands
 * here: same email-OTP auth as sending files, then their spaces (one per
 * practice relationship), threads (one per case/patient), messages with
 * reply, and the files/transfers the practice shared. This is the page the
 * practice-side webLinkFor() has pointed at since day one — "once the
 * frontend supports it". It does now.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getSession, listSpaces, listSpaceThreads, getSpaceThread,
  postSpaceMessage, markSpaceRead,
} from '../api';
import { EmailStep } from './Upload';

function fmtWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function SpacesPage() {
  const [authed, setAuthed] = useState(() => Boolean(getSession()?.token));
  const [searchParams] = useSearchParams();

  return (
    <div className="page">
      {!authed ? (
        <EmailStep onVerified={() => setAuthed(true)} />
      ) : (
        <SpacesBrowser
          deepSpace={searchParams.get('space')}
          deepThread={searchParams.get('thread')}
        />
      )}
    </div>
  );
}

function SpacesBrowser({ deepSpace, deepThread }) {
  const [spaces, setSpaces] = useState(null);
  const [active, setActive] = useState(null);   // { space, threads }
  const [thread, setThread] = useState(null);   // thread detail payload
  const [err, setErr] = useState(null);

  const openSpace = useCallback(async (space) => {
    setErr(null);
    try {
      const r = await listSpaceThreads(space.id);
      setActive({ space, threads: r.threads || [] });
      setThread(null);
      markSpaceRead(space.id).catch(() => {});
    } catch (e) { setErr(e.message); }
  }, []);

  const openThread = useCallback(async (spaceId, threadId) => {
    setErr(null);
    try {
      const r = await getSpaceThread(spaceId, threadId);
      setThread({ spaceId, ...r });
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await listSpaces();
        const list = r.spaces || [];
        setSpaces(list);
        // Deep link from an invite email: open the exact conversation.
        if (deepSpace) {
          const s = list.find((x) => x.id === deepSpace);
          if (s) {
            await openSpace(s);
            if (deepThread) await openThread(deepSpace, deepThread);
          }
        }
      } catch (e) { setErr(e.message); }
    })();
  }, [deepSpace, deepThread, openSpace, openThread]);

  if (spaces === null) {
    return <div className="narrow card" style={{ margin: '60px auto', textAlign: 'center' }}>Loading your conversations…</div>;
  }

  if (thread) {
    return (
      <ThreadView
        data={thread}
        onBack={() => setThread(null)}
        onRefresh={() => openThread(thread.spaceId, thread.thread.id)}
      />
    );
  }

  if (active) {
    return (
      <div className="narrow" style={{ margin: '40px auto' }}>
        <button className="btn btn-secondary" onClick={() => setActive(null)}>← All practices</button>
        <div className="card" style={{ marginTop: 12 }}>
          <h2 className="card-title">{active.space.title || active.space.counterparty_email || 'Conversation'}</h2>
          <p className="muted">Cases and conversations shared with you.</p>
          {active.threads.length === 0 && <p className="muted">No conversations yet.</p>}
          {active.threads.map((t) => (
            <button
              key={t.id}
              className="btn btn-secondary btn-block"
              style={{ textAlign: 'left', marginTop: 8 }}
              onClick={() => openThread(active.space.id, t.id)}
            >
              <strong>{t.patient_label || t.title || 'General'}</strong>
              <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
                {t.last_message_at ? fmtWhen(t.last_message_at) : ''}
              </span>
            </button>
          ))}
        </div>
        {err && <div className="error">{err}</div>}
      </div>
    );
  }

  return (
    <div className="narrow" style={{ margin: '40px auto' }}>
      <div className="card">
        <h2 className="card-title">Your case conversations</h2>
        <p className="muted">
          Practices that share cases with you appear here — messages, photos,
          and scan files, all in one secure place.
        </p>
        {spaces.length === 0 && (
          <p className="muted">
            Nothing here yet. When a practice invites you to a case, it shows
            up on this page (and you'll get an email).
          </p>
        )}
        {spaces.map((s) => (
          <button
            key={s.id}
            className="btn btn-secondary btn-block"
            style={{ textAlign: 'left', marginTop: 8 }}
            onClick={() => openSpace(s)}
          >
            <strong>{s.title || s.counterparty_email || 'Practice'}</strong>
            {s.unread_count > 0 && (
              <span style={{ marginLeft: 8, background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 12 }}>
                {s.unread_count}
              </span>
            )}
          </button>
        ))}

        {/* Whoever is reading this is usually a lab or a specialist receiving
            cases — a group the partner program explicitly pays for introducing
            practices. Placed under the list rather than above it: someone came
            here to read a case, not to be sold to. */}
        <div
          style={{
            marginTop: 20, padding: 14, borderRadius: 10,
            border: '1px solid #a7f3d0', background: '#f0fdf4',
          }}
        >
          <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 14 }}>
            Know a practice that should be sending cases like this?
          </p>
          <p className="muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
            Introduce them and earn <strong>$250 plus 10% of their subscription
            for a year</strong>. Open to anyone — labs, specialists, office
            managers and staff, not just practice owners.
          </p>
          <a
            className="btn btn-secondary"
            href="https://intake.dental/partners/for-dental-labs?utm_source=send&utm_medium=product&utm_campaign=partner_referral"
            target="_blank"
            rel="noopener noreferrer"
          >
            See what you'd earn →
          </a>
        </div>
      </div>
      {err && <div className="error">{err}</div>}
    </div>
  );
}

function ThreadView({ data, onBack, onRefresh }) {
  const { thread, messages = [], transfers = [], files = [] } = data;
  const me = getSession()?.user?.email?.toLowerCase();
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const send = async () => {
    const body = composer.trim();
    if (!body || sending) return;
    setSending(true);
    setErr(null);
    try {
      await postSpaceMessage(data.spaceId, thread.id, body);
      setComposer('');
      await onRefresh();
    } catch (e) { setErr(e.message); }
    setSending(false);
  };

  return (
    <div className="narrow" style={{ margin: '40px auto' }}>
      <button className="btn btn-secondary" onClick={onBack}>← Back</button>
      <div className="card" style={{ marginTop: 12 }}>
        <h2 className="card-title">{thread.patient_label || thread.title || 'Conversation'}</h2>

        {(transfers.length > 0 || files.length > 0) && (
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 14px', margin: '10px 0' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 14 }}>Files in this case</p>
            {transfers.map((t) => (
              <div key={t.id} style={{ fontSize: 13, margin: '4px 0' }}>
                📦 <strong>{t.title || 'File package'}</strong> — {t.file_count} file{t.file_count === 1 ? '' : 's'}
                {t.total_bytes ? ` (${fmtBytes(t.total_bytes)})` : ''} · sent {fmtWhen(t.created_at)}
                <span className="muted"> — download link was emailed to you</span>
              </div>
            ))}
            {files.map((f) => (
              <div key={f.id} style={{ fontSize: 13, margin: '4px 0' }}>
                📎 {f.original_filename} {f.size_bytes ? `(${fmtBytes(f.size_bytes)})` : ''}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '14px 0' }}>
          {messages.length === 0 && <p className="muted">No messages yet — say hello below.</p>}
          {messages.map((m) => {
            const mine = (m.sender_email || '').toLowerCase() === me;
            const system = m.kind && m.kind !== 'text';
            return (
              <div
                key={m.id}
                style={{
                  alignSelf: system ? 'center' : mine ? 'flex-end' : 'flex-start',
                  maxWidth: system ? '100%' : '85%',
                  background: system ? '#f3f4f6' : mine ? '#0e94a8' : '#f9fafb',
                  color: system ? '#6b7280' : mine ? '#fff' : '#111827',
                  border: system || mine ? 'none' : '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: system ? '4px 12px' : '8px 12px',
                  fontSize: system ? 12.5 : 14,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {!mine && !system && (
                  <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 2, color: '#6b7280' }}>
                    {m.sender_display_name || m.sender_email || 'Practice'}
                  </div>
                )}
                {m.body}
                <div style={{ fontSize: 10.5, opacity: 0.65, marginTop: 3 }}>{fmtWhen(m.created_at)}</div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <textarea
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical' }}
          />
          <button className="btn btn-primary" onClick={send} disabled={sending || !composer.trim()}>
            {sending ? '…' : 'Send'}
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span className="muted" style={{ fontSize: 12 }}>🔒 Encrypted and BAA-covered — safe for case details.</span>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={onRefresh}>Refresh</button>
        </div>
        {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  );
}
